import { useCallback, useEffect, useMemo } from 'react';

import { STRINGS } from '@/constants/strings';
import {
  PlayerProfileData,
  PlayerProfileDoc,
  PlayerProfileNoteData,
} from '@/features/player/components/types';
import {
  useCoachPlayerProfileQuery,
  useUpdateCoachPlayerMutation,
} from '@/features/player/queries';
import { sortProgressItemBadges } from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import { buildPlayerAvatarUri } from '@/features/player/utils/avatar-uri';
import { usePushCoachActivityMutation } from '@/queries/use-push-coach-activity-mutation';
import { buildActivityEventId } from '@/services/push-coach-activity';
import { useUiStore } from '@/stores';
import { useSessionStore } from '@/stores/session-store';
import { format, isValid, parse, parseISO } from 'date-fns';
import { useShallow } from 'zustand/shallow';

const TEST_PLAYER_ID = 'E6938A70-B54C-4743-A956-224EDF83940A';

export const DEFAULT_PROFILE: PlayerProfileData = {
  id: TEST_PLAYER_ID,
  name: '-',
  ageText: '',
  level: 1,
  inactive: false,
  parentName: '-',
  phone: '-',
  email: '-',
  notes: [],
  progress: [],
  welcomeKit: [],
  clubsneeded: false,
  dominantHand: '',
  height: '',
};

function toAgeText(dob?: PlayerProfileDoc['dob']) {
  if (!dob?.seconds) return DEFAULT_PROFILE.ageText;
  const birthDate = new Date(dob.seconds * 1000);
  if (Number.isNaN(birthDate.getTime())) return DEFAULT_PROFILE.ageText;

  const now = new Date();
  let age = now.getFullYear() - birthDate.getFullYear();
  const monthDiff = now.getMonth() - birthDate.getMonth();
  const hasNotHadBirthdayYet =
    monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate());
  if (hasNotHadBirthdayYet) {
    age -= 1;
  }

  if (age < 0) return DEFAULT_PROFILE.ageText;
  const ageUnit =
    age === 1 ? STRINGS.player.profile.ageYearOld : STRINGS.player.profile.ageYearsOld;
  return `${age} ${ageUnit}`;
}

function mapPlayerToProfileData(player: PlayerProfileDoc): PlayerProfileData {
  const name =
    player.name ??
    [player.firstname, player.lastname].filter(Boolean).join(' ').trim() ??
    DEFAULT_PROFILE.name;

  const parentName =
    [player.parent_first, player.parent_last].filter(Boolean).join(' ').trim() ||
    DEFAULT_PROFILE.parentName;

  const parseNoteDate = (raw: string): Date => {
    const trimmed = raw.trim();
    if (!trimmed) return new Date(0);

    // Existing systems may store ISO; keep supporting it.
    if (trimmed.includes('T')) {
      const iso = parseISO(trimmed);
      if (isValid(iso)) return iso;
    }

    // iOS legacy format: "Thursday, March 26, 2026"
    // Some legacy entries miss the space after comma before year: ",2026" -> ", 2026"
    const normalized = trimmed.replace(/,(\d{4})$/, ', $1');
    const parsed = parse(normalized, 'EEEE, MMMM d, yyyy', new Date());
    return isValid(parsed) ? parsed : new Date(0);
  };

  const notes: PlayerProfileNoteData[] = (player.notes ?? []).map((note, index) => {
    const parsedDate = parseNoteDate(note.date);
    const rawDateNormalized = note.date.trim().replace(/,(\d{4})$/, ', $1');
    return {
      id: `${player.id}-note-${index}`,
      title: note.title,
      detail: note.body,
      coachName: note.coachName,
      createdAt: parsedDate,
      rawDate: rawDateNormalized,
    };
  });

  const progress = (player.progress ?? []).map(sortProgressItemBadges);

  const welcomeKit = [...(player.welcomeKit ?? [])].reverse().map(kit => {
    return {
      level: kit.level ?? 0,
      date: kit.date ? format(new Date(kit.date), 'MM/dd/yyyy') : '',
      coachName: kit.coachName ?? '',
    };
  });

  return {
    id: player.id,
    name: name || DEFAULT_PROFILE.name,
    ageText: toAgeText(player.dob),
    level: player.level as number,
    inactive: Boolean(player.inactive),
    avatarUri:
      buildPlayerAvatarUri({
        playerId: player.id,
        avatarUrl: player.avatar_url ?? player.photo_url ?? player.image_url ?? null,
        avatarUpdatedAt: player.avatarUpdatedAt ?? null,
      }) ?? undefined,
    lastAttended: player.last_attended ?? undefined,
    parentName,
    phone: player.phone ?? player.alternate_phone ?? DEFAULT_PROFILE.phone,
    email: player.email ?? DEFAULT_PROFILE.email,
    notes,
    progress,
    welcomeKit,
    clubsneeded: player.clubsneeded ?? DEFAULT_PROFILE.clubsneeded,
    dominantHand:
      player.dominanthand !== undefined && player.dominanthand !== null
        ? player.dominanthand
          ? 'left'
          : 'right'
        : DEFAULT_PROFILE.dominantHand,
    height: player.height ? `${player.height}` : DEFAULT_PROFILE.height,
  };
}

export function usePlayerProfile(playerId?: string) {
  const resolvedPlayerId = playerId ?? '';
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const sessionUser = useSessionStore(s => s.user);
  const { mutate: logActivity } = usePushCoachActivityMutation();
  const { mutateAsync: updateCoachPlayer } = useUpdateCoachPlayerMutation();
  const {
    data,
    error: queryError,
    isSuccess,
    isError,
    isLoading,
    isRefetching,
    refetch,
  } = useCoachPlayerProfileQuery(resolvedPlayerId || undefined);

  useEffect(() => {
    if (!resolvedPlayerId) {
      hideGlobalLoading();
      return;
    }

    if (isLoading || isRefetching) {
      showGlobalLoading();
      return;
    }

    hideGlobalLoading();
  }, [hideGlobalLoading, isLoading, resolvedPlayerId, isRefetching, showGlobalLoading]);

  const profile = useMemo((): PlayerProfileData => {
    if (!resolvedPlayerId) return DEFAULT_PROFILE;
    if (isError) return DEFAULT_PROFILE;
    if (isSuccess && data) return mapPlayerToProfileData(data);
    return DEFAULT_PROFILE;
  }, [resolvedPlayerId, isError, isSuccess, data]);

  const error = useMemo((): string | null => {
    if (!resolvedPlayerId) return null;
    if (isError && queryError) {
      return queryError instanceof Error ? queryError.message : 'Failed to load player';
    }
    if (isSuccess && data === null) return 'Player not found';
    return null;
  }, [resolvedPlayerId, isError, isSuccess, queryError, data]);

  const saveNote = useCallback(
    async (payload: { title: string; detail: string }) => {
      if (!resolvedPlayerId) throw new Error('Missing playerId');
      const title = payload.title.trim();
      const detail = payload.detail.trim();
      if (!title || !detail) throw new Error('Missing note title/detail');

      showGlobalLoading();
      try {
        const player = data;
        if (!player) throw new Error('Player not found');

        const coachName = (sessionUser?.displayName ?? '').trim();
        const now = new Date();
        const nextNotes = [
          ...(player.notes ?? []),
          { title, body: detail, coachName, date: now.toISOString() },
        ];

        await updateCoachPlayer({
          playerId: resolvedPlayerId,
          data: { notes: nextNotes },
        });

        // PP-339 §4 Coach interactions — push the `coach_note_added` event. Coach +
        // kid_name are resolved server-side, so we only send the event key + player.
        // Best-effort: `mutate` never throws, so a failed push won't break the note.
        logActivity({
          eventId: buildActivityEventId(
            'player',
            resolvedPlayerId,
            'coach_note_added',
            now.toISOString(),
          ),
          type: 'coach_note_added',
          playerId: resolvedPlayerId,
        });
      } finally {
        hideGlobalLoading();
      }
    },
    [
      data,
      hideGlobalLoading,
      logActivity,
      resolvedPlayerId,
      sessionUser?.displayName,
      showGlobalLoading,
      updateCoachPlayer,
    ],
  );

  return useMemo(
    () => ({
      profile,
      error,
      refetch,
      isRefetching,
      playerId: resolvedPlayerId,
      saveNote,
    }),
    [profile, error, refetch, isRefetching, resolvedPlayerId, saveNote],
  );
}
